import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { motion, useScroll, useSpring } from "motion/react";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import SmoothScroll from "@/components/SmoothScroll";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import ChatWidget from "@/components/ChatWidget";
import Home from "@/pages/Home";
import Inventory from "@/pages/Inventory";
import CarDetail from "@/pages/CarDetail";
import LoanCalculatorPage from "@/pages/LoanCalculatorPage";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import TradeIn from "@/pages/TradeIn";
import Notices from "@/pages/Notices";
import Login from "@/pages/Login";
import Admin from "@/pages/Admin";
import Tietosuoja from "@/pages/Tietosuoja";
import Evasteet from "@/pages/Evasteet";
import Kayttoehdot from "@/pages/Kayttoehdot";
import Yritystiedot from "@/pages/Yritystiedot";
import BrandKit from "@/pages/BrandKit";

function ScrollToTop() {
  const { pathname } = useLocation();
  // Braces matter here: a concise-body effect returns whatever scrollTo()
  // returns. That's normally undefined, but on some Android browsers (and
  // with the platform's injected instrumentation script) it can come back
  // non-undefined, which React then calls as the effect's cleanup function
  // on the next navigation. Calling a non-function throws "destroy is not
  // a function" INSIDE React's commit phase, uncaught, which unmounts the
  // whole app — the page goes black and only a manual refresh recovers.
  // This was the real cause of the Android black-screen-on-navigation bug.
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

function RedirectHomeOnLoad() {
  const navigate = useNavigate();
  useEffect(() => {
    // Show homepage first only on the initial visit of a browser session.
    // After that, refreshing any page keeps you there.
    if (!sessionStorage.getItem("lak_visited")) {
      sessionStorage.setItem("lak_visited", "1");
      const path = window.location.pathname;
      const noRedirect = ["/", "/admin", "/admin/brandkit", "/login"];
      if (!noRedirect.includes(path)) navigate("/", { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
}

function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.2 });
  return <motion.div style={{ scaleX }} className="fixed top-0 left-0 right-0 h-[2px] bg-[#ff5a00] origin-left z-[70]" />;
}

function PageFade({ children }) {
  const { pathname } = useLocation();
  // Pure CSS animation (see .page-fade in index.css): compositor-driven,
  // so a stalled JS frame can never leave the page invisible on weak devices.
  return (
    <div key={pathname} className="page-fade">
      {children}
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SmoothScroll>
            <ScrollToTop />
            <RedirectHomeOnLoad />
            <ScrollProgress />
            <Navbar />
            <main>
              <PageFade>
                <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/autot" element={<Inventory />} />
                <Route path="/autot/:id" element={<CarDetail />} />
                <Route path="/rahoituslaskuri" element={<LoanCalculatorPage />} />
                <Route path="/tiedotteet" element={<Notices />} />
                <Route path="/vaihtoauto" element={<TradeIn />} />
                <Route path="/meista" element={<About />} />
                <Route path="/yhteystiedot" element={<Contact />} />
                <Route path="/login" element={<Login />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/admin/brandkit" element={<BrandKit />} />
                <Route path="/tietosuoja" element={<Tietosuoja />} />
                <Route path="/evasteet" element={<Evasteet />} />
                <Route path="/kayttoehdot" element={<Kayttoehdot />} />
                <Route path="/yritystiedot" element={<Yritystiedot />} />
                </Routes>
              </PageFade>
            </main>
            <Footer />
            <ChatWidget />
          </SmoothScroll>
          <Toaster theme="dark" position="top-right" toastOptions={{ style: { background: "#121212", border: "1px solid rgba(255,90,0,0.4)", color: "#fff", borderRadius: 0 } }} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
